import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { YT_STATE, type YTPlayer } from '@/utils/youtube-api';
import { setupTracker } from './youtube-tracker-internals';

type Args = {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  enabled: boolean;
  threshold: number;
  onWatched: () => void;
  onProgress?: (elapsed: number, duration: number) => void;
};

export type YTControls = {
  togglePlay: () => void;
  isPlaying: boolean;
};

function toggle(p: YTPlayer | null): void {
  if (!p) return;
  if (p.getPlayerState() === YT_STATE.PLAYING) p.pauseVideo();
  else p.playVideo();
}

function makeOnceFire(cbRef: { current: () => void }): () => void {
  let fired = false;
  return () => {
    if (fired) return;
    fired = true;
    cbRef.current();
  };
}

export function useYouTubeTracker(args: Args): YTControls {
  const { iframeRef, enabled, threshold, onWatched, onProgress } = args;
  const cbRef = useRef(onWatched);
  const progressRef = useRef(onProgress);
  const playerRef = useRef<YTPlayer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  useEffect(() => {
    cbRef.current = onWatched;
    progressRef.current = onProgress;
  }, [onWatched, onProgress]);
  useEffect(() => {
    if (!enabled) return;
    return setupTracker({
      iframe: iframeRef.current,
      threshold,
      fire: makeOnceFire(cbRef),
      onPlayer: (p) => (playerRef.current = p),
      onPlaying: setIsPlaying,
      onProgress: (e, d) => progressRef.current?.(e, d),
    });
  }, [iframeRef, enabled, threshold]);
  const togglePlay = useCallback(() => toggle(playerRef.current), []);
  return { togglePlay, isPlaying };
}
